import { useEffect, useState } from "react";
import { ApiError, get, post } from "../api";
import { GAME_META, ErrorBox, Field, Spinner } from "../components/ui";
import { Link, navigate } from "../router";
import { HeroBubble, PageHero } from "../components/PageHero";
import { LuSchool, LuUsers, LuRadio, LuLogOut } from "react-icons/lu";
import { useApp, useLoad } from "../store";

export function ClassroomStudent() {
  const { toast } = useApp();
  const { data, error, loading, reload } = useLoad(() => get<{ classes: any[] }>("/api/classes/mine"), []);
  const [code, setCode] = useState("");
  useEffect(() => { const id = setInterval(reload, 5000); return () => clearInterval(id); }, [reload]);
  const join = async (e: any) => {
    e.preventDefault();
    try { const r = await post<{ name: string }>("/api/classes/join", { code }); toast(`Du er nu med i ${r.name}`, "ok"); setCode(""); await reload(); } catch (er) { toast(er instanceof ApiError ? er.message : "Noget gik galt.", "error"); }
  };
  const leave = async (id: number) => {
    if (!confirm("Vil du forlade klassen?")) return;
    try { await post(`/api/classes/${id}/leave`); await reload(); } catch (er) { toast(er instanceof ApiError ? er.message : "Noget gik galt.", "error"); }
  };
  if (loading && !data) return <Spinner />;
  if (error) return <ErrorBox error={error} retry={reload} />;
  const live = data!.classes.filter((c) => c.session).length;
  return (
    <div className="stack rise">
      <PageHero
        tone="klass"
        title="Min klasse"
        icon={<LuSchool size={44} />}
        sub="Skriv koden fra din lærer."
      >
        <HeroBubble icon={<LuUsers aria-hidden="true" size={22} />} tone="blue" value={data!.classes.length} label={data!.classes.length === 1 ? "klasse" : "klasser"} />
        {live > 0 && <HeroBubble icon={<LuRadio aria-hidden="true" size={22} />} tone="pink" value="Live nu" label={`${live} seance${live > 1 ? "r" : ""}`} />}
      </PageHero>
      <form className="card row codecard" onSubmit={join}>
        <Field label="Klassekode" hint="6 tegn fra din lærer"><input value={code} maxLength={6} onChange={(e: any) => setCode(e.target.value.toUpperCase())} autoComplete="off" style={{ textTransform: "uppercase", letterSpacing: 3 }} /></Field>
        <button className="btn" disabled={code.length !== 6}>Tilmeld</button>
      </form>
      {data!.classes.length === 0 && (
        <div className="emptycard">
          <span className="emptyico" aria-hidden="true">🏫</span>
          <div>
            <b>Du er ikke med i nogen klasse endnu.</b>
            <p className="muted">Spørg din lærer om klassekoden. Du kan stadig spille alle spil og få mønter, mens du venter.</p>
          </div>
        </div>
      )}
      <div className="grid cards classgrid">
        {data!.classes.map((c, n) => (
          <div key={c.id} className={`card stack classcard${c.session ? " live" : ""}`} style={{ ["--i" as any]: n }}>
            <div className="row between"><h2 style={{ margin: 0 }}>{c.name}</h2><button className="btn ghost small" onClick={() => leave(c.id)}><LuLogOut aria-hidden="true" size={16} /> Forlad klassen</button></div>
            <p className="muted">{c.grade}. klasse · din lærer ser dit brugernavn og dine resultater</p>
            {c.session ? (
              <div className="notice good" role="status">
                <strong>{GAME_META[c.session.game]?.name ?? c.session.game}</strong> er i gang i klassen!{" "}
                <Link to={`/seance/${c.session.id}`} className="btn lime small">{c.session.joined ? "Gå til seancen" : "Deltag"}</Link>
              </div>
            ) : <p className="muted">Ingen aktiv seance lige nu.</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function SessionStudent({ id }: { id: number }) {
  const { toast } = useApp();
  const { data, error, loading, reload } = useLoad(() => get<any>(`/api/sessions/${id}`), [id]);
  useEffect(() => { const t = setInterval(reload, 1500); return () => clearInterval(t); }, [reload]);
  useEffect(() => { if (data?.matchId) navigate(`/kamp/${data.matchId}`, true); }, [data?.matchId]);
  if (loading && !data) return <Spinner />;
  if (error) return <ErrorBox error={error} retry={reload} />;
  const s = data!;
  const join = async () => { try { await post(`/api/sessions/${id}/join`); await reload(); } catch (er) { toast(er instanceof ApiError ? er.message : "Noget gik galt.", "error"); } };
  return (
    <div className="stack rise">
      <PageHero tone="klass" compact title={GAME_META[s.game]?.name ?? s.game} icon={<LuSchool size={40} />} sub="Seance i din klasse">
        <HeroBubble icon={<LuUsers aria-hidden="true" size={22} />} tone="blue" value={s.participantCount} label="er med" />
      </PageHero>
      <div className="card stack center lobbycard">
      {s.status === "finished" ? <p>Seancen er slut. <Link to="/klasse">Tilbage til klassen</Link></p> : s.joined ? (
        <p role="status">Du er med! Venter på at din lærer starter spillet… ({s.participantCount} er med)</p>
      ) : s.status === "lobby" ? (
        <button className="btn lime" onClick={join}>Deltag i seancen</button>
      ) : <p className="notice warn">Seancen er allerede i gang, og du kan ikke komme med nu.</p>}
      </div>
    </div>
  );
}
